import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Lock, Info } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import type { ForecastResponse, ForecastRow, ForecastConfidence } from '../../types/nursery';

/**
 * /nursery/forecast — how many saleable trees the growing crop will give, per
 * stock item and season.
 *
 * A lot's count is a field count. The server takes off the tree type's loss
 * for the best count the lot has had (bud, 1st, 2nd), splits what is left
 * across the grades by their expected shares until the crop is graded, and
 * says how sure it is: the weakest lot's count. Nothing here is stored; it
 * moves as soon as a count is recorded.
 */

const CONFIDENCE: Record<ForecastConfidence, { label: string; className: string }> = {
  1: { label: 'Bud count', className: 'bg-amber-100 text-amber-800' },
  2: { label: '1st count', className: 'bg-yellow-100 text-yellow-800' },
  3: { label: '2nd count', className: 'bg-lime-100 text-lime-800' },
  4: { label: 'Graded', className: 'bg-green-100 text-green-800' },
};

const n = (v: number) => v.toLocaleString();

function treeName(r: ForecastRow) {
  return [r.cultivar_name, r.rootstock_name].filter(Boolean).join(' / ');
}

function ForecastCard({ row, gradeNames }: { row: ForecastRow; gradeNames: { grade_rank: number; name: string }[] }) {
  const conf = CONFIDENCE[row.confidence];
  const grades = gradeNames.filter((g) => row.by_grade[g.grade_rank] != null);
  const notCounted = row.counts.none;
  return (
    <li className="bg-white border border-neutral-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-neutral-900 break-words">{treeName(row)}</p>
          <p className="text-xs text-neutral-500 break-words">{row.tree_type_name}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-neutral-500">Expected</p>
          <p className="text-lg font-semibold text-neutral-900">{n(row.expected)}</p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-600">
        <span className={cn('rounded-full px-2 py-0.5 font-medium', conf.className)}>{conf.label}</span>
        <span>
          {n(row.trees_counted)} in the rows · {row.lots} lot{row.lots === 1 ? '' : 's'}
          {notCounted > 0 && ` · ${notCounted} not counted yet`}
        </span>
      </div>
      {grades.length > 0 && (
        <p className="mt-2 text-sm text-neutral-700 break-words">
          {grades.map((g) => `${g.name} ${n(row.by_grade[g.grade_rank])}`).join(' · ')}
          {row.graded_trees > 0 && row.graded_trees < row.expected && (
            <span className="text-neutral-500"> ({n(row.graded_trees)} graded)</span>
          )}
        </p>
      )}
    </li>
  );
}

function NurseryForecast() {
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stockType, setStockType] = useState<'' | 'B' | 'P'>('B');
  const [season, setSeason] = useState<number | 'none' | ''>('');

  useEffect(() => {
    if (!hasAccess) return;
    setData(null);
    setError(null);
    nurseryService.getForecast({ stock_type: stockType || undefined })
      .then((res) => {
        if (!res.success) { setError(errorMessage(res, 'Could not load the forecast')); return; }
        setData(res.data);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the forecast')));
  }, [hasAccess, stockType]);

  // Open on the first season that has not finished; it is the one being sold.
  const thisYear = new Date().getFullYear();
  const shownSeason = season !== '' ? season
    : data?.seasons.find((s) => s >= thisYear) ?? data?.seasons[data.seasons.length - 1] ?? 'none';

  const rows = useMemo(() => (data?.rows ?? []).filter((r) =>
    shownSeason === 'none' ? r.season == null : r.season === shownSeason), [data, shownSeason]);
  const hasUndated = (data?.rows ?? []).some((r) => r.season == null);
  const total = rows.reduce((s, r) => s + r.expected, 0);
  const counted = rows.reduce((s, r) => s + r.trees_counted, 0);

  if (licenseLoading) {
    return <div className="max-w-3xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery seat required</h2>
          <p className="text-sm text-neutral-500">The forecast needs a nursery operator, manager or admin seat.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Expected stock" subtitle="What the growing crop will give, by season" icon={<TrendingUp className="h-6 w-6 text-white" />} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm text-neutral-700 min-w-0">
            Season
            <select
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={String(shownSeason)}
              onChange={(e) => setSeason(e.target.value === 'none' ? 'none' : Number(e.target.value))}
              disabled={!data}
            >
              {(data?.seasons ?? []).map((s) => <option key={s} value={s}>{s} season</option>)}
              {hasUndated && <option value="none">No ready date</option>}
            </select>
          </label>
          <label className="text-sm text-neutral-700 min-w-0">
            Stock
            <select
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={stockType}
              onChange={(e) => { setStockType(e.target.value as '' | 'B' | 'P'); setSeason(''); }}
            >
              <option value="B">Bare root</option>
              <option value="P">Container</option>
              <option value="">All stock</option>
            </select>
          </label>
        </div>

        <details className="bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-sm text-neutral-700">
          <summary className="cursor-pointer flex items-center gap-1.5 font-medium">
            <Info className="h-4 w-4 flex-shrink-0" /> How this is worked out
          </summary>
          <div className="mt-2 space-y-1.5 break-words">
            <p>
              Each lot&apos;s count, less the share its tree type expects to lose after its best count (Tree Types: after the bud
              count, the 1st count or the 2nd). Graded and ready stock counts in full.
            </p>
            <p>Until a crop is graded, what is expected is split across the grades by their expected shares.</p>
            <p>
              The label is how sure it is: the roughest count among the lots. Record a 1st or 2nd count on
              {' '}<Link to="/nursery/stocktake" className="text-primary-700 underline">Stocktake</Link> to firm it up.
            </p>
          </div>
        </details>

        {!data && !error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />}

        {data && (
          rows.length === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
              Nothing is growing for this season.
            </div>
          ) : (
            <>
              <p className="text-sm text-neutral-700">
                <span className="font-semibold">{n(total)}</span> trees expected from {n(counted)} in the rows,
                across {rows.length} line{rows.length === 1 ? '' : 's'}.
              </p>
              <ul className="space-y-3">
                {rows.map((r) => (
                  <ForecastCard
                    key={`${r.cultivar_id}:${r.rootstock_id ?? 0}:${r.tree_type_id}:${r.season ?? 'none'}`}
                    row={r}
                    gradeNames={data.grades[String(r.tree_type_id)] ?? []}
                  />
                ))}
              </ul>
            </>
          )
        )}
      </div>
    </>
  );
}

export default NurseryForecast;
